if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        if (this.dataset.productFormBound === 'true') return;
        this.dataset.productFormBound = 'true';

        this.form = this.querySelector('form');
        // Shopify Payment Button より「カートに追加」(name=add) を優先。なければ submit ボタン。
        this.submitButton =
          this.querySelector('button[name="add"]') ||
          this.querySelector('.product-form__submit') ||
          this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span') || null;

        if (!this.form || !this.submitButton) return;

        this.syncVariantInputDisabledState();
        this.observePaymentButtonInjection();
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) {
          this.submitButton =
            this.querySelector('button[name="add"]') ||
            this.querySelector('.product-form__submit') ||
            this.querySelector('[type="submit"]');
          this.submitButtonText = this.submitButton?.querySelector('span') || null;
        }
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }

        // variant input は有効のまま（Shopify Payment Button の描画に必要）
        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.syncPaymentButton(disable);
      }

      syncVariantInputDisabledState() {
        if (!this.submitButton || !this.variantIdInput) return;
        this.variantIdInput.disabled = false;
        const shouldDisableButtons = this.submitButton.hasAttribute('disabled');
        this.syncPaymentButton(shouldDisableButtons);
      }

      syncPaymentButton(disable) {
        const paymentContainer = this.form?.querySelector('.shopify-payment-button');
        if (!paymentContainer) return;

        paymentContainer.classList.toggle('shopify-payment-button--disabled', disable);

        paymentContainer.querySelectorAll('button, [role="button"]').forEach((button) => {
          if (disable) {
            /* div[role=button] に disabled 属性は HTML として不正。button のみ付与 */
            if (button.tagName === 'BUTTON') {
              button.setAttribute('disabled', 'disabled');
            }
            button.setAttribute('aria-disabled', 'true');
            button.setAttribute('tabindex', '-1');
          } else {
            button.removeAttribute('disabled');
            button.removeAttribute('aria-disabled');
            button.removeAttribute('tabindex');
          }
        });
      }

      observePaymentButtonInjection() {
        const buttonsContainer = this.form?.querySelector('.product-form__buttons');
        if (!buttonsContainer) return;

        this.paymentButtonObserver = new MutationObserver(() => {
          if (!this.submitButton) return;
          this.syncPaymentButton(this.submitButton.hasAttribute('disabled'));
        });

        this.paymentButtonObserver.observe(buttonsContainer, { childList: true, subtree: true });
      }

      get variantIdInput() {
        return this.form?.querySelector('[name=id]');
      }
    }
  );
}
